import React, { useState, useEffect, useRef } from 'react';
import './LoadingAnimations.css';

// Loading stage definitions
const LOADING_STAGES = [
  { id: 'locate', icon: 'fi-rr-marker', label: 'מאתר את הנכס', labelEn: 'Locating property' },
  { id: 'parcel', icon: 'fi-rr-vector-polygon', label: 'מביא גיאומטריית חלקה', labelEn: 'Getting parcel geometry' },
  { id: 'municipal', icon: 'fi-rr-bank', label: 'מחפש במאגרי העיריות', labelEn: 'Searching municipal databases' },
  { id: 'plans', icon: 'fi-rr-document', label: 'טוען תוכניות', labelEn: 'Loading plans' },
  { id: 'market', icon: 'fi-rr-chart-line-up', label: 'מביא נתוני שוק', labelEn: 'Fetching market data' },
  { id: 'calculate', icon: 'fi-rr-calculator', label: 'מחשב כדאיות', labelEn: 'Calculating feasibility' },
];

// Helper to determine current stage from status text
const getStageFromStatus = (status) => {
  if (!status) return 0;
  
  // Stage 5: Feasibility calculation (check first - most specific)
  if (status.includes('כדאיות') || status.includes('ניתוח')) return 5;
  
  // Stage 4: Market data
  if (status.includes('שוק') || status.includes('מחיר')) return 4;
  
  // Stage 3: Plans/documents (check before municipal - "ממשלתי" is plans)
  if (status.includes('תוכניות') || status.includes('ממשלתי')) return 3;
  
  // Stage 2: Municipal data
  if (status.includes('רשות מקומית') || status.includes('עיריות') || status.includes('עירוני') || status.includes('מאגרי')) return 2;
  
  // Stage 1: Parcel geometry
  if (status.includes('חלקה') || status.includes('גיאומטרי')) return 1;
  
  // Stage 0: Locating property (default for מאתר, נכס, מתחיל, etc.)
  return 0;
};

// =============================================================================
// ANIMATED ICONS - Unique animation for each stage
// =============================================================================

// 1. Location Pin - Pin dropping onto a map
const AnimatedPin = ({ isActive, isCurrent }) => (
  <div className={`animated-icon pin-icon ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
    <svg viewBox="0 0 60 60" className="icon-svg" style={{ overflow: 'visible' }}>
      {/* Map background */}
      <rect className="map-bg" x="5" y="30" width="50" height="28" rx="3" />
      {/* Map streets */}
      <line className="map-street street-h1" x1="5" y1="40" x2="55" y2="40" />
      <line className="map-street street-h2" x1="5" y1="50" x2="55" y2="50" />
      <line className="map-street street-v1" x1="20" y1="30" x2="20" y2="58" />
      <line className="map-street street-v2" x1="40" y1="30" x2="40" y2="58" />
      {/* Map parcels */}
      <rect className="map-parcel parcel-1" x="8" y="33" width="10" height="5" rx="1" />
      <rect className="map-parcel parcel-2" x="42" y="42" width="10" height="6" rx="1" />
      <rect className="map-parcel parcel-3" x="23" y="52" width="14" height="4" rx="1" />
      {/* Target location highlight */}
      <circle className="target-glow" cx="30" cy="44" r="8" />
      <circle className="target-pulse" cx="30" cy="44" r="6" />
      {/* Pin - drops from above */}
      <g className="pin-group">
        <path className="pin-shadow" d="M26 44 Q30 48 34 44 Q30 46 26 44" />
        <path className="pin-body" d="M30 4 C23 4 17 10 17 17 C17 26 30 38 30 38 C30 38 43 26 43 17 C43 10 37 4 30 4 Z" />
        <circle className="pin-center" cx="30" cy="16" r="6" />
      </g>
    </svg>
  </div>
);

// 2. Parcel Geometry - Survey/measurement animation
const AnimatedParcel = ({ isActive, isCurrent }) => (
  <div className={`animated-icon parcel-icon ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
    <svg viewBox="0 0 60 60" className="icon-svg">
      {/* Background grid pattern */}
      <defs>
        <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#e2e8f0" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect className="grid-bg" x="5" y="5" width="50" height="50" fill="url(#grid)" rx="4"/>
      
      {/* Connecting lines - draw between points */}
      <path className="connect-line line-1" d="M 12,38 L 20,15" />
      <path className="connect-line line-2" d="M 20,15 L 42,12" />
      <path className="connect-line line-3" d="M 42,12 L 50,32" />
      <path className="connect-line line-4" d="M 50,32 L 38,48" />
      <path className="connect-line line-5" d="M 38,48 L 12,38" />
      
      {/* Filled parcel area */}
      <polygon className="parcel-fill" points="12,38 20,15 42,12 50,32 38,48" />
      
      {/* Survey points with pulsing effect */}
      <g className="survey-point p1">
        <circle className="point-ring" cx="12" cy="38" r="6" />
        <circle className="point-center" cx="12" cy="38" r="3" />
      </g>
      <g className="survey-point p2">
        <circle className="point-ring" cx="20" cy="15" r="6" />
        <circle className="point-center" cx="20" cy="15" r="3" />
      </g>
      <g className="survey-point p3">
        <circle className="point-ring" cx="42" cy="12" r="6" />
        <circle className="point-center" cx="42" cy="12" r="3" />
      </g>
      <g className="survey-point p4">
        <circle className="point-ring" cx="50" cy="32" r="6" />
        <circle className="point-center" cx="50" cy="32" r="3" />
      </g>
      <g className="survey-point p5">
        <circle className="point-ring" cx="38" cy="48" r="6" />
        <circle className="point-center" cx="38" cy="48" r="3" />
      </g>
    </svg>
  </div>
);

// 3. Municipal Building - Building with papers flying out
const AnimatedBuilding = ({ isActive, isCurrent }) => (
  <div className={`animated-icon building-icon ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
    <svg viewBox="0 0 60 60" className="icon-svg" style={{ overflow: 'visible' }}>
      {/* Building */}
      <g className="building-group">
        <rect className="building-base" x="14" y="22" width="32" height="30" rx="2" />
        <polygon className="building-roof" points="10,22 30,8 50,22" />
        {/* Windows */}
        <rect className="window" x="18" y="28" width="6" height="6" rx="1" />
        <rect className="window" x="27" y="28" width="6" height="6" rx="1" />
        <rect className="window" x="36" y="28" width="6" height="6" rx="1" />
        {/* Door */}
        <rect className="door" x="25" y="40" width="10" height="12" rx="1" />
      </g>
      {/* Flying papers - positioned to fly from building */}
      <rect className="paper paper-1" x="30" y="30" width="7" height="9" rx="1" />
      <rect className="paper paper-2" x="30" y="30" width="6" height="8" rx="1" />
      <rect className="paper paper-3" x="30" y="30" width="5" height="7" rx="1" />
    </svg>
  </div>
);

// 4. Documents - Shuffling/stacking papers with animated text lines
const AnimatedDocuments = ({ isActive, isCurrent }) => (
  <div className={`animated-icon documents-icon ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
    <svg viewBox="0 0 60 60" className="icon-svg">
      {/* Stacking documents */}
      <g className="doc-stack">
        <rect className="doc doc-3" x="18" y="8" width="28" height="36" rx="2" />
        <rect className="doc doc-2" x="14" y="12" width="28" height="36" rx="2" />
        <rect className="doc doc-1" x="10" y="16" width="28" height="36" rx="2" />
        {/* Lines on front doc - animated */}
        <line className="doc-line l1" x1="16" y1="26" x2="32" y2="26" />
        <line className="doc-line l2" x1="16" y1="32" x2="28" y2="32" />
        <line className="doc-line l3" x1="16" y1="38" x2="30" y2="38" />
        <line className="doc-line l4" x1="16" y1="44" x2="26" y2="44" />
      </g>
    </svg>
  </div>
);

// 5. Money/Market - Coins dropping into bag
const AnimatedMoney = ({ isActive, isCurrent }) => (
  <div className={`animated-icon money-icon ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
    <svg viewBox="0 0 60 60" className="icon-svg" style={{ overflow: 'visible' }}>
      {/* Money bag */}
      <g className="money-bag">
        {/* Bag body */}
        <path className="bag-body" d="M16 30 Q10 40 12 52 Q16 58 30 58 Q44 58 48 52 Q50 40 44 30 Z" />
        {/* Bag opening */}
        <ellipse className="bag-opening" cx="30" cy="30" rx="14" ry="5" />
        {/* Bag tie/knot */}
        <path className="bag-tie" d="M20 30 Q24 22 30 25 Q36 22 40 30" fill="none" strokeWidth="3" strokeLinecap="round" />
        {/* Shekel symbol on bag */}
        <text className="bag-symbol" x="30" y="48" textAnchor="middle">₪</text>
      </g>
      {/* Coins that drop in from above */}
      <g className="dropping-coins">
        <circle className="drop-coin coin-1" cx="30" cy="5" r="5" />
        <circle className="drop-coin coin-2" cx="24" cy="8" r="4" />
        <circle className="drop-coin coin-3" cx="36" cy="6" r="4" />
      </g>
    </svg>
  </div>
);

// 6. Calculate/Magic - Calculator with typing numbers animation
const AnimatedCalculate = ({ isActive, isCurrent }) => (
  <div className={`animated-icon calculate-icon ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
    <svg viewBox="0 0 60 60" className="icon-svg" style={{ overflow: 'visible' }}>
      {/* Sparkles */}
      <path className="sparkle s1" d="M6 10 L8 6 L10 10 L8 14 Z" />
      <path className="sparkle s2" d="M50 6 L52 2 L54 6 L52 10 Z" />
      <path className="sparkle s3" d="M54 48 L56 44 L58 48 L56 52 Z" />
      {/* Calculator body */}
      <g className="calculator">
        <rect className="calc-body" x="10" y="8" width="40" height="48" rx="4" />
        {/* Screen */}
        <rect className="calc-screen" x="14" y="12" width="32" height="14" rx="2" />
        {/* Clip path for screen content */}
        <clipPath id="screenClip">
          <rect x="14" y="12" width="32" height="14" rx="2" />
        </clipPath>
        {/* Numbers inside screen - clipped */}
        <g clipPath="url(#screenClip)">
          <text className="calc-digit d1" x="19" y="22">3</text>
          <text className="calc-digit d2" x="24" y="22">5</text>
          <text className="calc-digit d3" x="28" y="22">,</text>
          <text className="calc-digit d4" x="31" y="22">0</text>
          <text className="calc-digit d5" x="36" y="22">0</text>
          <text className="calc-digit d6" x="41" y="22">0</text>
          {/* Cursor blink - inside screen */}
          <rect className="calc-cursor" x="44" y="14" width="1" height="10" />
        </g>
        {/* Buttons - 4x3 grid */}
        <rect className="calc-btn btn-7" x="14" y="30" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-8" x="23" y="30" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-9" x="32" y="30" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-div" x="41" y="30" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-4" x="14" y="37" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-5" x="23" y="37" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-6" x="32" y="37" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-mul" x="41" y="37" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-1" x="14" y="44" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-2" x="23" y="44" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-3" x="32" y="44" width="7" height="5" rx="1" />
        <rect className="calc-btn btn-eq" x="41" y="44" width="7" height="9" rx="1" />
        <rect className="calc-btn btn-0" x="14" y="51" width="16" height="4" rx="1" />
        <rect className="calc-btn btn-dot" x="32" y="51" width="7" height="4" rx="1" />
      </g>
    </svg>
  </div>
);

// Array of animated icon components
const AnimatedIcons = [AnimatedPin, AnimatedParcel, AnimatedBuilding, AnimatedDocuments, AnimatedMoney, AnimatedCalculate];

// =============================================================================
// OPTION A: Vertical Timeline (Delightful Version!)
// =============================================================================
const MIN_STAGE_DISPLAY_MS = 800; // Minimum time to show each stage

export const TimelineLoading = ({ status, isLoadingComplete = false, onAnimationComplete }) => {
  const actualStage = getStageFromStatus(status);
  const [displayedStage, setDisplayedStage] = useState(0);
  const [maxReachedStage, setMaxReachedStage] = useState(0);
  const [completedAnimations, setCompletedAnimations] = useState({});
  const [hasFinished, setHasFinished] = useState(false);
  const lastAdvanceTimeRef = useRef(Date.now());
  const timerRef = useRef(null);
  
  // Track the maximum stage from actual status (never go backwards)
  useEffect(() => {
    if (actualStage > maxReachedStage) {
      setMaxReachedStage(actualStage);
    }
  }, [actualStage, maxReachedStage]);
  
  // When loading completes, target the final stage
  const targetStage = isLoadingComplete 
    ? LOADING_STAGES.length - 1 
    : Math.max(maxReachedStage, displayedStage);
  
  // Advance displayed stage towards target with minimum display time
  useEffect(() => {
    // If we've finished all stages, mark as done
    if (displayedStage >= LOADING_STAGES.length - 1) {
      if (!hasFinished) {
        const finishTimer = setTimeout(() => {
          setCompletedAnimations(prev => ({ ...prev, [LOADING_STAGES.length - 1]: true }));
          setHasFinished(true);
        }, MIN_STAGE_DISPLAY_MS);
        return () => clearTimeout(finishTimer);
      }
      return;
    }
    
    // Need to advance to target
    if (displayedStage < targetStage) {
      const timeSinceLastAdvance = Date.now() - lastAdvanceTimeRef.current;
      const delay = Math.max(0, MIN_STAGE_DISPLAY_MS - timeSinceLastAdvance);
      
      timerRef.current = setTimeout(() => {
        setCompletedAnimations(prev => ({ ...prev, [displayedStage]: true }));
        setDisplayedStage(prev => prev + 1);
        lastAdvanceTimeRef.current = Date.now();
      }, delay);
      
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }
  }, [displayedStage, targetStage, hasFinished]);
  
  // Call onAnimationComplete when both loading is done AND animation has finished
  useEffect(() => {
    if (isLoadingComplete && hasFinished && onAnimationComplete) {
      const completeTimer = setTimeout(onAnimationComplete, 300);
      return () => clearTimeout(completeTimer);
    }
  }, [isLoadingComplete, hasFinished, onAnimationComplete]);
  
  // Use displayedStage for rendering
  const currentStage = displayedStage;
  
  // Calculate progress percentage (RTL: 100% = right, 0% = left)
  const progressPercent = (currentStage / (LOADING_STAGES.length - 1)) * 100;
  
  return (
    <div className="loading-animation timeline-loading timeline-horizontal">
      {/* Background decorations */}
      <div className="timeline-bg-shapes">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
      </div>
      
      <div className="timeline-container">
        {/* Progress line behind everything */}
        <div className="timeline-line-bg">
          <div 
            className="timeline-progress" 
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        
        {/* All stages in a row - each stage is a column with icon, dot, label */}
        <div className="timeline-stages">
          {LOADING_STAGES.map((stage, index) => {
            const AnimatedIcon = AnimatedIcons[index];
            const isActive = index <= currentStage;
            const isCurrent = index === currentStage;
            const isCompleted = completedAnimations[index];
            
            return (
              <div 
                key={stage.id}
                className={`timeline-stage ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                {/* Icon */}
                <div className="timeline-icon-wrapper">
                  <div className="timeline-icon animated-icon-container">
                    <AnimatedIcon isActive={isActive} isCurrent={isCurrent} />
                  </div>
                  {isCurrent && <div className="timeline-pulse"></div>}
                  {isCompleted && <div className="timeline-checkmark">✓</div>}
                </div>
                
                {/* Dot - centered below icon */}
                <div className={`timeline-dot ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`} />
                
                {/* Label */}
                <span className="timeline-label">{stage.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Current status text */}
      <div className="loading-status-text timeline-status">
        <span className="status-step">{currentStage + 1}/{LOADING_STAGES.length}</span>
        {LOADING_STAGES[currentStage]?.label || 'טוען'}
        <span className="loading-dots"></span>
      </div>
    </div>
  );
};

// =============================================================================
// OPTION B: Circular Progress
// =============================================================================
export const CircularLoading = ({ status }) => {
  const currentStage = getStageFromStatus(status);
  const progress = ((currentStage + 1) / LOADING_STAGES.length) * 100;
  const circumference = 2 * Math.PI * 90;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  
  return (
    <div className="loading-animation circular-loading">
      <div className="circular-container">
        <svg className="circular-progress" viewBox="0 0 200 200">
          {/* Background circle */}
          <circle
            className="circular-bg"
            cx="100"
            cy="100"
            r="90"
            fill="none"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            className="circular-fill"
            cx="100"
            cy="100"
            r="90"
            fill="none"
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        
        {/* Center content */}
        <div className="circular-center">
          <div className="circular-icon-active">
            <i className={`fi ${LOADING_STAGES[currentStage]?.icon || 'fi-rr-spinner'}`}></i>
          </div>
          <div className="circular-percentage">{Math.round(progress)}%</div>
        </div>
        
        {/* Orbiting icons */}
        <div className="circular-icons">
          {LOADING_STAGES.map((stage, index) => {
            const angle = (index / LOADING_STAGES.length) * 360 - 90;
            const radian = (angle * Math.PI) / 180;
            const x = 100 + 90 * Math.cos(radian);
            const y = 100 + 90 * Math.sin(radian);
            
            return (
              <div
                key={stage.id}
                className={`circular-stage-icon ${index <= currentStage ? 'active' : ''} ${index === currentStage ? 'current' : ''}`}
                style={{
                  left: `${x}px`,
                  top: `${y}px`,
                }}
              >
                <i className={`fi ${stage.icon}`}></i>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="loading-status-text">
        {status || 'מתחיל...'}
        <span className="loading-dots"></span>
      </div>
    </div>
  );
};

// =============================================================================
// OPTION C: Building Construction
// =============================================================================
export const BuildingLoading = ({ status }) => {
  const currentStage = getStageFromStatus(status);
  
  return (
    <div className="loading-animation building-loading">
      <div className="building-scene">
        {/* Ground */}
        <div className="building-ground"></div>
        
        {/* Crane */}
        <div className={`building-crane ${currentStage >= 0 ? 'active' : ''}`}>
          <div className="crane-tower"></div>
          <div className="crane-arm"></div>
          <div className="crane-cable"></div>
          <div className="crane-hook"></div>
        </div>
        
        {/* Building floors */}
        <div className="building-structure">
          {LOADING_STAGES.map((stage, index) => (
            <div 
              key={stage.id}
              className={`building-floor ${index <= currentStage ? 'built' : ''} ${index === currentStage ? 'building' : ''}`}
              style={{ '--floor-index': index }}
            >
              <div className="floor-content">
                <i className={`fi ${stage.icon}`}></i>
                <span>{stage.label}</span>
              </div>
              <div className="floor-windows">
                <div className="window"></div>
                <div className="window"></div>
                <div className="window"></div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Particles */}
        <div className="building-particles">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="particle" style={{ '--particle-index': i }}></div>
          ))}
        </div>
      </div>
      
      <div className="loading-status-text building-status">
        <span className="status-icon">🏗️</span>
        {status || 'מתחיל לבנות...'}
        <span className="loading-dots"></span>
      </div>
    </div>
  );
};

// =============================================================================
// OPTION D: Map Data Points
// =============================================================================
export const MapLoading = ({ status }) => {
  const currentStage = getStageFromStatus(status);
  
  // City positions on a stylized Israel map
  const cities = [
    { name: 'חיפה', x: 35, y: 25 },
    { name: 'תל אביב', x: 30, y: 45 },
    { name: 'ירושלים', x: 50, y: 55 },
    { name: 'באר שבע', x: 45, y: 78 },
    { name: 'אילת', x: 55, y: 95 },
  ];
  
  return (
    <div className="loading-animation map-loading">
      <div className="map-container">
        {/* Stylized Israel outline */}
        <svg className="map-outline" viewBox="0 0 100 120">
          <path
            className="map-path"
            d="M35 5 L45 5 L50 15 L55 10 L60 20 L55 35 L60 50 L55 65 L60 80 L55 95 L50 110 L45 115 L40 100 L35 85 L30 70 L25 55 L30 40 L25 25 L30 15 Z"
            fill="none"
            strokeWidth="2"
          />
        </svg>
        
        {/* Data collection radar */}
        <div className="map-radar">
          <div className="radar-sweep"></div>
          <div className="radar-pulse"></div>
        </div>
        
        {/* City markers */}
        {cities.map((city, index) => (
          <div
            key={city.name}
            className={`map-city ${index <= currentStage ? 'active' : ''} ${index === currentStage ? 'current' : ''}`}
            style={{ left: `${city.x}%`, top: `${city.y}%` }}
          >
            <div className="city-marker">
              <div className="marker-pin"></div>
              <div className="marker-pulse"></div>
            </div>
            <span className="city-name">{city.name}</span>
          </div>
        ))}
        
        {/* Connection lines */}
        <svg className="map-connections" viewBox="0 0 100 120">
          {cities.slice(0, currentStage + 1).map((city, index) => {
            if (index === 0) return null;
            const prev = cities[index - 1];
            return (
              <line
                key={`line-${index}`}
                className="connection-line"
                x1={prev.x}
                y1={prev.y}
                x2={city.x}
                y2={city.y}
                strokeWidth="1"
                strokeDasharray="4 2"
              />
            );
          })}
        </svg>
        
        {/* Data icons floating */}
        <div className="map-data-icons">
          {LOADING_STAGES.map((stage, index) => (
            <div
              key={stage.id}
              className={`data-icon ${index <= currentStage ? 'collected' : ''}`}
              style={{ '--icon-index': index }}
            >
              <i className={`fi ${stage.icon}`}></i>
            </div>
          ))}
        </div>
      </div>
      
      <div className="loading-status-text map-status">
        <span className="status-icon">🗺️</span>
        {status || 'סורק את המפה...'}
        <span className="loading-dots"></span>
      </div>
    </div>
  );
};

// =============================================================================
// HEADER-STYLE OPTIONS - Compact loading for header/search area
// =============================================================================

// Option 1: Slim Progress Bar with dots
export const SlimProgressBar = ({ status }) => {
  const currentStage = getStageFromStatus(status);
  const progress = ((currentStage + 1) / LOADING_STAGES.length) * 100;
  
  return (
    <div className="slim-progress-container">
      <div className="slim-progress-bar">
        <div className="slim-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="slim-progress-dots">
        {LOADING_STAGES.map((stage, index) => (
          <div 
            key={stage.id}
            className={`slim-dot ${index <= currentStage ? 'active' : ''} ${index === currentStage ? 'current' : ''}`}
            title={stage.label}
          >
            {index < currentStage && <i className="fi fi-rr-check" />}
          </div>
        ))}
      </div>
      <div className="slim-status">{LOADING_STAGES[currentStage]?.label}<span className="loading-dots"></span></div>
    </div>
  );
};

// Option 2: Compact Strip with mini icons
export const CompactStrip = ({ status }) => {
  const currentStage = getStageFromStatus(status);
  
  return (
    <div className="compact-strip-container">
      <div className="compact-strip">
        {LOADING_STAGES.map((stage, index) => (
          <div 
            key={stage.id}
            className={`compact-stage ${index <= currentStage ? 'active' : ''} ${index === currentStage ? 'current' : ''}`}
          >
            <div className="compact-icon">
              <i className={`fi ${stage.icon}`} />
            </div>
            {index < LOADING_STAGES.length - 1 && (
              <div className={`compact-connector ${index < currentStage ? 'filled' : ''}`} />
            )}
          </div>
        ))}
      </div>
      <div className="compact-label">{LOADING_STAGES[currentStage]?.label}<span className="loading-dots"></span></div>
    </div>
  );
};

// Helper to get animated icon by stage id
const getAnimatedIconForStage = (stageId, isActive, isCurrent) => {
  const iconMap = {
    'locate': <AnimatedPin isActive={isActive} isCurrent={isCurrent} />,
    'parcel': <AnimatedParcel isActive={isActive} isCurrent={isCurrent} />,
    'municipal': <AnimatedBuilding isActive={isActive} isCurrent={isCurrent} />,
    'plans': <AnimatedDocuments isActive={isActive} isCurrent={isCurrent} />,
    'market': <AnimatedMoney isActive={isActive} isCurrent={isCurrent} />,
    'calculate': <AnimatedCalculate isActive={isActive} isCurrent={isCurrent} />,
  };
  return iconMap[stageId] || null;
};

// Short labels for compact display
const SHORT_LABELS = {
  'locate': 'מיקום',
  'parcel': 'חלקה',
  'municipal': 'עירייה',
  'plans': 'תוכניות',
  'market': 'שוק',
  'calculate': 'כדאיות',
};

// Option 3: Search Button Transform with Animated Icons
export const ButtonTransform = ({ status }) => {
  const currentStage = getStageFromStatus(status);
  const progress = ((currentStage + 1) / LOADING_STAGES.length) * 100;
  const currentStageData = LOADING_STAGES[currentStage];
  
  return (
    <div className="button-transform-container">
      {/* Main button with animated icon */}
      <div className="transform-button">
        <div className="button-progress" style={{ width: `${progress}%` }} />
        
        {/* Animated icon section */}
        <div className="button-icon-wrapper">
          {getAnimatedIconForStage(currentStageData?.id, true, true) || (
            <i className={`fi ${currentStageData?.icon || 'fi-rr-search'}`} />
          )}
        </div>
        
        {/* Text content */}
        <div className="button-text-content">
          <span className="button-label">{currentStageData?.label}<span className="loading-dots"></span></span>
          <span className="button-step-text">שלב {currentStage + 1} מתוך {LOADING_STAGES.length}</span>
        </div>
      </div>
      
      {/* Stage indicator dots below */}
      <div className="button-stage-dots">
        {LOADING_STAGES.map((stage, index) => (
          <div 
            key={stage.id}
            className={`stage-dot ${index <= currentStage ? 'active' : ''} ${index === currentStage ? 'current' : ''}`}
            title={stage.label}
          />
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// INTEGRATED SEARCH BUTTON LOADING - Transforms into a visual loading state
// =============================================================================
export const SearchButtonLoading = ({ status, isScrolled = false, isLoadingComplete = false, onAnimationComplete }) => {
  const actualStage = getStageFromStatus(status);
  const [displayedStage, setDisplayedStage] = useState(0);
  const [hasFinished, setHasFinished] = useState(false);
  const [canAdvance, setCanAdvance] = useState(false);
  const timerRef = useRef(null);
  const maxActualStage = useRef(0);
  
  const MIN_STAGE_DISPLAY_MS = 1200; // Minimum time per stage - nice and readable
  const CATCH_UP_MS = 600; // When catching up at the end
  const TOTAL_STAGES = LOADING_STAGES.length;
  
  // Track the highest actual stage reached
  useEffect(() => {
    if (actualStage > maxActualStage.current) {
      maxActualStage.current = actualStage;
    }
  }, [actualStage]);
  
  // Timer to allow advancing - resets each time displayedStage changes
  useEffect(() => {
    setCanAdvance(false);
    
    const timer = setTimeout(() => {
      setCanAdvance(true);
    }, isLoadingComplete && displayedStage < TOTAL_STAGES - 1 ? CATCH_UP_MS : MIN_STAGE_DISPLAY_MS);
    
    return () => clearTimeout(timer);
  }, [displayedStage, isLoadingComplete, TOTAL_STAGES]);
  
  // Advance when allowed and needed
  useEffect(() => {
    if (!canAdvance) return;
    
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Determine target stage
    let targetStage = maxActualStage.current;
    if (isLoadingComplete) {
      targetStage = TOTAL_STAGES - 1;
    }
    
    // Advance if behind target
    if (displayedStage < targetStage) {
      timerRef.current = setTimeout(() => {
        setDisplayedStage(prev => prev + 1);
      }, 50); // Small delay for smooth transition
    }
    // Finish if at end and loading complete
    else if (displayedStage === TOTAL_STAGES - 1 && isLoadingComplete && !hasFinished) {
      timerRef.current = setTimeout(() => {
        setHasFinished(true);
        if (onAnimationComplete) onAnimationComplete();
      }, 100);
    }
    
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [canAdvance, displayedStage, isLoadingComplete, hasFinished, onAnimationComplete, TOTAL_STAGES]);
  
  const currentStage = displayedStage;
  const currentStageData = LOADING_STAGES[currentStage];
  
  return (
    <div className={`search-button-loading morphing ${isScrolled ? 'scrolled' : ''}`}>
      {/* Spinning border */}
      <div className="spin-border" />
      
      {/* Inner content - icon */}
      <div className="morph-content">
        <div className="morph-icon">
          {getAnimatedIconForStage(currentStageData?.id, true, true) || (
            <i className={`fi ${currentStageData?.icon || 'fi-rr-search'}`} />
          )}
        </div>
      </div>
      
      {/* Speech bubble with status text */}
      <div className={`status-bubble ${isScrolled ? 'bubble-bottom' : ''}`} key={currentStage}>
        <span className="bubble-text">{currentStageData?.label || 'מחפש...'}</span>
        <span className="bubble-step">{currentStage + 1}/{LOADING_STAGES.length}</span>
        <div className="bubble-arrow" />
      </div>
    </div>
  );
};

// NEW: Inline Search Input Loading - replaces entire search bar during loading
export const SearchInputLoading = ({ status, isScrolled = false, isLoadingComplete = false, onAnimationComplete }) => {
  const actualStage = getStageFromStatus(status);
  const [displayedStage, setDisplayedStage] = useState(0);
  const [hasFinished, setHasFinished] = useState(false);
  const [canAdvance, setCanAdvance] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);
  const timerRef = useRef(null);
  const maxActualStage = useRef(0);
  // const initialScrolledState = useRef(isScrolled);
  
  // Track if we've done the initial animation (only animate once on mount)
  useEffect(() => {
    // Mark animation as done after it completes (500ms for desktop, 400ms for mobile)
    const animationDuration = isMobile ? 400 : 500;
    const timer = setTimeout(() => {
      setHasAnimatedIn(true);
    }, animationDuration);
    return () => clearTimeout(timer);
  }, [isMobile]); // Re-run if breakpoint changes
  
  // Detect mobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const MIN_STAGE_DISPLAY_MS = 1200;
  const CATCH_UP_MS = 600;
  const TOTAL_STAGES = LOADING_STAGES.length;
  
  // Track the highest actual stage reached
  useEffect(() => {
    if (actualStage > maxActualStage.current) {
      maxActualStage.current = actualStage;
    }
  }, [actualStage]);
  
  // Timer to allow advancing
  useEffect(() => {
    setCanAdvance(false);
    const timer = setTimeout(() => {
      setCanAdvance(true);
    }, isLoadingComplete && displayedStage < TOTAL_STAGES - 1 ? CATCH_UP_MS : MIN_STAGE_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [displayedStage, isLoadingComplete, TOTAL_STAGES]);
  
  // Advance when allowed and needed
  useEffect(() => {
    if (!canAdvance) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    
    let targetStage = maxActualStage.current;
    if (isLoadingComplete) targetStage = TOTAL_STAGES - 1;
    
    if (displayedStage < targetStage) {
      timerRef.current = setTimeout(() => {
        setDisplayedStage(prev => prev + 1);
      }, 50);
    } else if (displayedStage === TOTAL_STAGES - 1 && isLoadingComplete && !hasFinished) {
      timerRef.current = setTimeout(() => {
        setHasFinished(true);
        if (onAnimationComplete) onAnimationComplete();
      }, 800); // Show final stage for a bit before finishing animation
    }
    
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [canAdvance, displayedStage, isLoadingComplete, hasFinished, onAnimationComplete, TOTAL_STAGES]);
  
  const currentStage = displayedStage;
  const currentStageData = LOADING_STAGES[currentStage];
  const IconComponent = getAnimatedIconForStage(currentStageData?.id, true, true);
  
  // Calculate progress percentage (0 to 100)
  const progressPercent = ((currentStage + 1) / TOTAL_STAGES) * 100;
  
  // Determine if we're finishing (use hasFinished which has a delay after reaching final stage)
  const isFinishing = hasFinished;
  
  // Build class name - add mobile-expanded class for mobile non-scrolled state
  const mobileExpandedClass = (isMobile && !isScrolled) ? 'mobile-expanded' : '';
  // Only animate on initial mount, not when switching between collapsed/expanded
  const animateInClass = !hasAnimatedIn ? 'animate-in' : '';
  
  return (
    <div 
      className={`search-input-loading ${isScrolled ? 'scrolled' : ''} ${isFinishing ? 'finishing' : ''} ${mobileExpandedClass} ${animateInClass}`}
    >
      {/* Progress fill from right to left */}
      <div 
        className="input-loading-progress" 
        style={{ width: `${progressPercent}%` }}
      />
      
      {/* Content container - RTL layout (text only, no icon) */}
      <div 
        className="input-loading-content"
        style={{ '--progress-width': `${progressPercent}%` }}
      >
        {/* Step counter - on the right (leading edge in RTL) */}
        <span className="input-loading-step">{currentStage + 1}/{TOTAL_STAGES}</span>
        
        {/* Status text */}
        <div className="input-loading-text">
          <span className="input-loading-label">{currentStageData?.label || 'מחפש...'}</span>
          <span className="input-loading-dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </div>
      </div>
      
      {/* Loading button with spinning circle and icon inside */}
      <div className="input-loading-btn">
        {/* Spinning border */}
        <div className="input-btn-spin-border" />
        {/* Icon inside */}
        <div className="input-btn-icon" key={`icon-${currentStage}`}>
          {IconComponent || <i className={`fi ${currentStageData?.icon || 'fi-rr-search'}`} />}
        </div>
      </div>
    </div>
  );
};

// Option 4: Header Border Progress with Animated Icons (Integrated)
export const HeaderBorderProgress = ({ status }) => {
  const currentStage = getStageFromStatus(status);
  
  return (
    <div className="header-border-container">
      <div className="header-border-demo">
        <div className="fake-header">
          <span className="fake-logo">🏗️ אורבניקס</span>
          <div className="fake-search">
            <i className="fi fi-rr-search" />
            <span>חיפוש כתובת...</span>
          </div>
        </div>
        
        {/* Integrated progress track with icons */}
        <div className="integrated-progress-track">
          {/* Background track */}
          <div className="track-line" />
          
          {/* Icons positioned along the track */}
          {LOADING_STAGES.map((stage, index) => {
            const isActive = index <= currentStage;
            const isCurrent = index === currentStage;
            const isCompleted = index < currentStage;
            const position = (index / (LOADING_STAGES.length - 1)) * 100;
            
            return (
              <div 
                key={stage.id} 
                className={`track-waypoint ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}
                style={{ right: `${position}%` }}
              >
                <div className="waypoint-icon">
                  {getAnimatedIconForStage(stage.id, isActive, isCurrent) || (
                    <i className={`fi ${stage.icon}`} />
                  )}
                </div>
                <span className="waypoint-label">{SHORT_LABELS[stage.id]}</span>
              </div>
            );
          })}
          
          {/* Animated progress fill */}
          <div 
            className="track-fill" 
            style={{ width: `${(currentStage / (LOADING_STAGES.length - 1)) * 100}%` }} 
          />
          
          {/* Moving glow indicator */}
          <div 
            className="track-glow"
            style={{ right: `${(currentStage / (LOADING_STAGES.length - 1)) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// SHOWCASE COMPONENT - Preview all options
// =============================================================================
export const LoadingShowcase = () => {
  const [activeOption, setActiveOption] = useState('A');
  const [demoStage, setDemoStage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  
  // Auto-advance demo
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setDemoStage(prev => (prev + 1) % LOADING_STAGES.length);
    }, 2000);
    
    return () => clearInterval(interval);
  }, [isPlaying]);
  
  const demoStatus = LOADING_STAGES[demoStage]?.label || 'מתחיל...';
  
  // Wrapper to show SearchButtonLoading in both states
  const SearchButtonPreview = ({ status }) => (
    <div className="search-button-preview">
      <div className="preview-state">
        <span className="state-label">מצב מורחב</span>
        <SearchButtonLoading status={status} isScrolled={false} />
      </div>
      <div className="preview-state">
        <span className="state-label">מצב מכווץ</span>
        <SearchButtonLoading status={status} isScrolled={true} />
      </div>
    </div>
  );
  
  const options = [
    { key: 'A', name: 'Current Timeline', nameHe: 'ציר זמן (נוכחי)', Component: TimelineLoading, category: 'full' },
    { key: 'B', name: 'Slim Progress', nameHe: 'פס דק', Component: SlimProgressBar, category: 'header' },
    { key: 'C', name: 'Compact Strip', nameHe: 'רצועה קומפקטית', Component: CompactStrip, category: 'header' },
    { key: 'D', name: 'Button Transform', nameHe: 'כפתור משתנה', Component: ButtonTransform, category: 'header' },
    { key: 'E', name: 'Header Border', nameHe: 'גבול כותרת', Component: HeaderBorderProgress, category: 'header' },
    { key: 'F', name: 'Search Button', nameHe: '✅ כפתור חיפוש (משולב)', Component: SearchButtonPreview, category: 'header' },
  ];
  
  const activeOptionData = options.find(o => o.key === activeOption);
  const ActiveComponent = activeOptionData?.Component || TimelineLoading;
  const isHeaderStyle = activeOptionData?.category === 'header';
  
  const fullOptions = options.filter(o => o.category === 'full');
  const headerOptions = options.filter(o => o.category === 'header');
  
  return (
    <div className="loading-showcase">
      <div className="showcase-header">
        <h2>🎨 Loading Animation Options</h2>
        <p>Choose where the loading animation should appear</p>
      </div>
      
      <div className="showcase-categories">
        <div className="category-section">
          <h3 className="category-title">📍 Results Area (Current)</h3>
          <p className="category-desc">Full-page animation in the results section</p>
          <div className="showcase-tabs">
            {fullOptions.map(option => (
              <button
                key={option.key}
                className={`showcase-tab ${activeOption === option.key ? 'active' : ''}`}
                onClick={() => setActiveOption(option.key)}
              >
                <span className="tab-name">{option.nameHe}</span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="category-section header-options">
          <h3 className="category-title">🔝 Header / Search Area (New Ideas)</h3>
          <p className="category-desc">Compact loading integrated with the header</p>
          <div className="showcase-tabs">
            {headerOptions.map(option => (
              <button
                key={option.key}
                className={`showcase-tab ${activeOption === option.key ? 'active' : ''}`}
                onClick={() => setActiveOption(option.key)}
              >
                <span className="tab-name">{option.nameHe}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className={`showcase-preview ${isHeaderStyle ? 'header-style-preview' : ''}`}>
        {isHeaderStyle && (
          <div className="preview-context-label">
            <i className="fi fi-rr-arrow-down"></i>
            This would appear under/in your search header
            <i className="fi fi-rr-arrow-down"></i>
          </div>
        )}
        <ActiveComponent status={demoStatus} />
        {isHeaderStyle && (
          <div className="preview-shimmer-hint">
            <div className="fake-shimmer-card">
              <div className="shimmer-line w60"></div>
              <div className="shimmer-line w80"></div>
              <div className="shimmer-line w40"></div>
            </div>
            <span>Results shimmer would show below ↓</span>
          </div>
        )}
      </div>
      
      <div className="showcase-controls">
        <button 
          className={`control-btn ${isPlaying ? 'playing' : ''}`}
          onClick={() => setIsPlaying(!isPlaying)}
        >
          <i className={`fi ${isPlaying ? 'fi-rr-pause' : 'fi-rr-play'}`}></i>
          {isPlaying ? 'Pause' : 'Play'}
        </button>
        <div className="stage-indicator">
          Stage {demoStage + 1} / {LOADING_STAGES.length}
        </div>
        <button 
          className="control-btn"
          onClick={() => setDemoStage((demoStage + 1) % LOADING_STAGES.length)}
        >
          <i className="fi fi-rr-forward"></i>
          Next
        </button>
      </div>
    </div>
  );
};

const LoadingAnimations = { 
  TimelineLoading, 
  CircularLoading, 
  BuildingLoading, 
  MapLoading, 
  SlimProgressBar,
  CompactStrip,
  ButtonTransform,
  HeaderBorderProgress,
  SearchButtonLoading,
  LoadingShowcase 
};

export default LoadingAnimations;

